import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PERMISSIONS,
  PERMISSION_LABELS,
  ROLE_LABELS,
  ROLE_PERMISSIONS,
  assignableRoles,
  isNetworkWide,
  type HiboutikAccountDTO,
  type InvitationDTO,
  type MemberDTO,
  type Permission,
  type Role,
} from "@easyshop/shared";
import {
  Avatar,
  Button,
  Card,
  Drawer,
  DrawerHeader,
  Empty,
  ErrorState,
  FieldLabel,
  Loading,
  Pill,
  SectionTitle,
  cx,
  inputClass,
} from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { api } from "@/lib/api";
import { useHiboutikAccounts, useInvitations, useMembers } from "@/lib/queries";
import { IosGroup, IosRow, IosSection } from "@/components/mobile/MobileUi";
import { dateFR, initials } from "@/lib/format";

// --- Aides -------------------------------------------------------------------

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  OWNER: "Tout : réseau, boutiques, comptes Hiboutik, accès et paramètres.",
  ADMIN: "Tout le réseau : pilotage, messages, tâches, campagnes, accès.",
  MANAGER: "Ses comptes Hiboutik et boutiques ; peut inviter son équipe.",
  STAFF: "Sa boutique : ventes, stock, messages et tâches à réaliser.",
  VIEWER: "Consultation seule des chiffres de son périmètre.",
};

const ROLE_PILL: Record<Role, string> = {
  OWNER: "bg-accent-strong text-white",
  ADMIN: "bg-positive-bg text-positive",
  MANAGER: "bg-accent-soft text-accent",
  STAFF: "bg-page text-[oklch(0.45_0.015_150)]",
  VIEWER: "bg-page text-muted-2",
};

function RolePill({ role }: { role: Role }) {
  return <Pill className={cx("text-[11px]", ROLE_PILL[role])}>{ROLE_LABELS[role]}</Pill>;
}

function absoluteLink(link: string | undefined): string {
  if (!link) return "";
  return link.startsWith("/") ? `${window.location.origin}${link}` : link;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Contexte non sécurisé : repli via une zone de texte temporaire.
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    ta.remove();
    return ok;
  }
}

function CopyButton({ text, label = "Copier le lien", className }: { text: string; label?: string; className?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() =>
        void copyText(text).then((ok) => {
          setDone(ok);
          if (ok) setTimeout(() => setDone(false), 2000);
        })
      }
      className={cx(
        "whitespace-nowrap rounded-full border border-line bg-white px-[14px] py-[7px] text-[12px] font-bold text-accent hover:bg-page",
        className,
      )}
    >
      {done ? "Copié ✓" : label}
    </button>
  );
}

/** Libellé du périmètre : « Tout le réseau » ou comptes + villes. */
function scopeLabel(
  role: Role,
  accountIds: string[],
  boutiqueIds: string[],
  accounts: HiboutikAccountDTO[],
  cityOf: (id: string) => string | undefined,
): { main: string; parts: string[] } {
  if (isNetworkWide(role)) return { main: "Tout le réseau", parts: [] };
  const parts = [
    ...accountIds.map((id) => {
      const a = accounts.find((x) => x.id === id);
      return a ? (/^compte\b/i.test(a.label) ? a.label : `Compte ${a.label}`) : "Compte Hiboutik";
    }),
    ...boutiqueIds.map((id) => cityOf(id)).filter((c): c is string => Boolean(c)),
  ];
  return { main: parts.length ? parts.join(" · ") : "Aucune boutique", parts };
}

function useInvalidateAccess() {
  const qc = useQueryClient();
  return () => {
    for (const k of ["members", "invitations", "me"]) void qc.invalidateQueries({ queryKey: [k] });
  };
}

// --- Formulaire commun (membre / invitation) -----------------------------------

interface AccessDraft {
  email: string;
  role: Role;
  hiboutikAccountIds: string[];
  boutiqueIds: string[];
  granted: Permission[];
  revoked: Permission[];
}

function toggle<T>(list: T[], v: T): T[] {
  return list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
}

function Check({
  checked,
  disabled,
  onChange,
  children,
  hint,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <label
      className={cx(
        "flex items-start gap-[10px] rounded-[10px] px-2 py-[7px] text-[13px]",
        disabled ? "opacity-60" : "cursor-pointer hover:bg-page",
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="mt-[2px] h-4 w-4 flex-shrink-0 accent-[var(--accent)]"
      />
      <span className="min-w-0 flex-1">
        <span className="font-semibold">{children}</span>
        {hint && <span className="block text-[11.5px] text-muted-2">{hint}</span>}
      </span>
    </label>
  );
}

function AccessFields({ draft, setDraft, withEmail }: { draft: AccessDraft; setDraft: (d: AccessDraft) => void; withEmail: boolean }) {
  const { me } = useSession();
  const accounts = useHiboutikAccounts();
  const roles = me.role ? assignableRoles(me.role) : [];
  const defaults = new Set<Permission>(ROLE_PERMISSIONS[draft.role]);
  const mine = new Set(me.permissions);
  const networkWide = isNetworkWide(draft.role);
  const viaAccount = (accountId: string) => draft.hiboutikAccountIds.includes(accountId);

  const effective = (p: Permission) => (defaults.has(p) && !draft.revoked.includes(p)) || draft.granted.includes(p);
  const togglePerm = (p: Permission) => {
    if (effective(p)) {
      setDraft(
        defaults.has(p) ? { ...draft, revoked: [...draft.revoked, p] } : { ...draft, granted: draft.granted.filter((x) => x !== p) },
      );
    } else {
      setDraft(
        defaults.has(p) ? { ...draft, revoked: draft.revoked.filter((x) => x !== p) } : { ...draft, granted: [...draft.granted, p] },
      );
    }
  };

  return (
    <>
      {withEmail && (
        <div>
          <FieldLabel>E-mail</FieldLabel>
          <input
            type="email"
            className={inputClass}
            placeholder="prenom.nom@exemple.fr"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
        </div>
      )}

      <div>
        <FieldLabel>Rôle</FieldLabel>
        <select
          className={inputClass}
          value={draft.role}
          onChange={(e) => {
            const role = e.target.value as Role;
            const def = new Set<Permission>(ROLE_PERMISSIONS[role]);
            setDraft({
              ...draft,
              role,
              granted: draft.granted.filter((p) => !def.has(p)),
              revoked: draft.revoked.filter((p) => def.has(p)),
            });
          }}
        >
          {roles.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <div className="mt-[6px] text-[11.5px] text-muted-2">{ROLE_DESCRIPTIONS[draft.role]}</div>
      </div>

      <div>
        <FieldLabel>Périmètre</FieldLabel>
        {networkWide ? (
          <div className="rounded-[10px] bg-accent-soft px-3 py-[10px] text-[12.5px] font-semibold text-accent">
            Tout le réseau — ce rôle voit toutes les boutiques.
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div>
              <div className="mb-1 text-[12px] font-semibold text-muted">Comptes Hiboutik</div>
              <div className="mb-1 text-[11.5px] text-muted-2">Accès à toutes les boutiques du compte, y compris futures.</div>
              {accounts.isLoading && <div className="text-[12px] text-muted">Chargement…</div>}
              {accounts.error && <ErrorState error={accounts.error} />}
              <div className="max-h-[260px] overflow-auto">
                {(accounts.data ?? []).map((a) => (
                  <Check
                    key={a.id}
                    checked={draft.hiboutikAccountIds.includes(a.id)}
                    onChange={() =>
                      setDraft({
                        ...draft,
                        hiboutikAccountIds: toggle(draft.hiboutikAccountIds, a.id),
                      })
                    }
                    hint={`${a.subdomain}.hiboutik.com · ${a.boutiques.map((b) => b.city).join(", ") || "aucune boutique"}`}
                  >
                    {a.label}
                  </Check>
                ))}
              </div>
              {accounts.data?.length === 0 && <div className="text-[12px] text-muted">Aucun compte Hiboutik.</div>}
            </div>
            <div>
              <div className="mb-1 text-[12px] font-semibold text-muted">Boutiques individuelles</div>
              <div className="max-h-[260px] overflow-auto">
                {me.boutiques.map((b) => {
                  const covered = b.hiboutikAccountId != null && viaAccount(b.hiboutikAccountId);
                  return (
                    <Check
                      key={b.id}
                      checked={covered || draft.boutiqueIds.includes(b.id)}
                      disabled={covered}
                      hint={covered ? "incluse via le compte Hiboutik" : undefined}
                      onChange={() =>
                        setDraft({
                          ...draft,
                          boutiqueIds: toggle(draft.boutiqueIds, b.id),
                        })
                      }
                    >
                      {b.city}
                      {b.name !== b.city && <span className="font-normal text-muted-2"> · {b.name}</span>}
                    </Check>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div>
        <FieldLabel>Permissions</FieldLabel>
        <div className="mb-1 text-[11.5px] text-muted-2">
          Cochées par défaut selon le rôle ; ajustez au cas par cas. Vous ne pouvez accorder que les permissions que vous avez.
        </div>
        <div className="flex flex-col">
          {PERMISSIONS.map((p) => {
            const on = effective(p);
            const changed = on !== defaults.has(p);
            return (
              <Check key={p} checked={on} disabled={!on && !mine.has(p)} onChange={() => togglePerm(p)}>
                {PERMISSION_LABELS[p]}
                {changed && (
                  <Pill className={cx("ml-2 align-middle", on ? "bg-positive-bg text-positive" : "bg-danger-bg text-danger")}>
                    {on ? "ajoutée" : "retirée"}
                  </Pill>
                )}
              </Check>
            );
          })}
        </div>
      </div>
    </>
  );
}

function payloadOf(d: AccessDraft) {
  const def = new Set<Permission>(ROLE_PERMISSIONS[d.role]);
  const wide = isNetworkWide(d.role);
  // Les boutiques déjà couvertes par un compte coché ne sont pas dupliquées.
  return {
    role: d.role,
    hiboutikAccountIds: wide ? [] : d.hiboutikAccountIds,
    boutiqueIds: wide ? [] : d.boutiqueIds,
    grantedPermissions: d.granted.filter((p) => !def.has(p)),
    revokedPermissions: d.revoked.filter((p) => def.has(p)),
  };
}

// --- Drawers ------------------------------------------------------------------------

function MemberDrawer({ member, onClose }: { member: MemberDTO; onClose: () => void }) {
  const invalidate = useInvalidateAccess();
  const { me } = useSession();
  const [draft, setDraft] = useState<AccessDraft>({
    email: member.user.email,
    role: member.role,
    hiboutikAccountIds: member.hiboutikAccountIds,
    boutiqueIds: member.boutiqueIds,
    granted: member.grantedPermissions,
    revoked: member.revokedPermissions,
  });
  const save = useMutation({
    mutationFn: () => {
      const p = payloadOf(draft);
      const same = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));
      // Un gérant ne gère que les boutiques de son périmètre : l'API conserve les autres.
      const visible = new Set(me.boutiques.map((b) => b.id));
      const boutiqueIds = p.boutiqueIds.filter((id) => me.networkWide || visible.has(id));
      return api.patch<MemberDTO>(`/members/${member.id}`, {
        role: p.role,
        grantedPermissions: p.grantedPermissions,
        revokedPermissions: p.revokedPermissions,
        // Périmètre envoyé seulement s'il a changé (évite d'écraser des accès hors de notre vue).
        ...(same(p.hiboutikAccountIds, member.hiboutikAccountIds) ? {} : { hiboutikAccountIds: p.hiboutikAccountIds }),
        ...(same(p.boutiqueIds, member.boutiqueIds) ? {} : { boutiqueIds }),
      });
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/members/${member.id}`),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  return (
    <Drawer open onClose={onClose} width={460}>
      <DrawerHeader title="Modifier l'accès" onClose={onClose} />
      <div className="flex items-center gap-3">
        <Avatar text={initials(member.user.name)} />
        <div className="min-w-0">
          <div className="truncate text-[14px] font-bold">{member.user.name}</div>
          <div className="truncate text-[12px] text-muted-2">{member.user.email}</div>
        </div>
      </div>
      <AccessFields draft={draft} setDraft={setDraft} withEmail={false} />
      {(save.error || remove.error) && <ErrorState error={save.error ?? remove.error} />}
      <div className="mt-auto flex flex-wrap gap-2 pt-2">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Enregistrement…" : "Enregistrer"}
        </Button>
        <Button variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <div className="flex-1" />
        <Button
          variant="ghost"
          className="!text-danger hover:!bg-danger-bg"
          disabled={remove.isPending}
          onClick={() => {
            if (window.confirm(`Retirer l'accès de ${member.user.name} au réseau ?`)) remove.mutate();
          }}
        >
          Retirer l'accès
        </Button>
      </div>
    </Drawer>
  );
}

function InviteDrawer({ onClose }: { onClose: () => void }) {
  const { me } = useSession();
  const invalidate = useInvalidateAccess();
  const roles = me.role ? assignableRoles(me.role) : [];
  const [draft, setDraft] = useState<AccessDraft>({
    email: "",
    role: roles.includes("STAFF") ? "STAFF" : (roles[0] ?? "VIEWER"),
    hiboutikAccountIds: [],
    boutiqueIds: me.boutiques.length === 1 ? [me.boutiques[0].id] : [],
    granted: [],
    revoked: [],
  });
  const create = useMutation({
    mutationFn: () =>
      api.post<InvitationDTO>("/invitations", {
        email: draft.email.trim(),
        ...payloadOf(draft),
      }),
    onSuccess: () => invalidate(),
  });
  const created = create.data;

  return (
    <Drawer open onClose={onClose} width={460}>
      <DrawerHeader title={created ? "Invitation créée" : "Inviter une personne"} onClose={onClose} />
      {created ? (
        <>
          <div className="rounded-card-sm bg-accent-soft px-4 py-[14px] text-[13px] leading-[1.5]">
            Invitation créée pour <b>{created.email}</b> ({ROLE_LABELS[created.role]}). Aucun e-mail n'est envoyé automatiquement :
            transmettez-lui ce lien (valable jusqu'au {dateFR(created.expiresAt)}).
          </div>
          <div>
            <FieldLabel>Lien d'invitation</FieldLabel>
            <input
              readOnly
              className={cx(inputClass, "text-[12px]")}
              value={absoluteLink(created.link)}
              onFocus={(e) => e.target.select()}
            />
          </div>
          <div className="flex gap-2">
            <CopyButton text={absoluteLink(created.link)} className="!bg-accent !text-white !border-accent" />
            <Button variant="outline" onClick={onClose}>
              Fermer
            </Button>
          </div>
        </>
      ) : (
        <>
          <AccessFields draft={draft} setDraft={setDraft} withEmail />
          {create.error && <ErrorState error={create.error} />}
          <div className="mt-auto flex gap-2 pt-2">
            <Button onClick={() => create.mutate()} disabled={create.isPending || !draft.email.trim()}>
              {create.isPending ? "Création…" : "Créer l'invitation"}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </div>
        </>
      )}
    </Drawer>
  );
}

// --- Page ---------------------------------------------------------------------------

export function AccessPage() {
  const { me, can } = useSession();
  const manage = can("members.manage");
  const members = useMembers();
  const invitations = useInvitations(manage);
  const accounts = useHiboutikAccounts();
  const invalidate = useInvalidateAccess();
  const [editing, setEditing] = useState<MemberDTO | null>(null);
  const [inviting, setInviting] = useState(false);
  const assignable = me.role ? assignableRoles(me.role) : [];
  const cityOf = useMemo(() => {
    const map = new Map(me.boutiques.map((b) => [b.id, b.city]));
    return (id: string) => map.get(id);
  }, [me.boutiques]);
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/invitations/${id}`),
    onSuccess: () => invalidate(),
  });

  const pending = (invitations.data ?? []).filter((i) => i.status === "PENDING");

  return (
    <div className="grid items-start gap-[18px] xl:grid-cols-[minmax(0,1fr)_320px]">
      <div className="flex min-w-0 flex-col gap-[18px]">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1 text-[13px] text-muted">
            {me.networkWide
              ? `Qui a accès au réseau ${me.organization?.name ?? ""}, avec quel rôle et sur quelles boutiques.`
              : "Votre équipe : les personnes qui ont accès à vos boutiques."}
          </div>
          {manage && <Button onClick={() => setInviting(true)}>+ Inviter</Button>}
        </div>

        {/* Membres : liste iOS, la même sur PC et mobile ; toucher un membre modifiable ouvre sa fiche. */}
        <IosSection title={members.data ? `Membres · ${members.data.length}` : "Membres"}>
          <IosGroup>
            {members.isLoading && <Loading />}
            {members.error && <ErrorState error={members.error} className="m-4" />}
            {members.data?.length === 0 && <Empty>Aucun membre.</Empty>}
            {(members.data ?? []).map((m) => {
              const self = m.user.id === me.user.id;
              const editable = manage && !self && assignable.includes(m.role);
              const scope = scopeLabel(m.role, m.hiboutikAccountIds, m.boutiqueIds, accounts.data ?? [], cityOf);
              const adjusted = m.grantedPermissions.length > 0 || m.revokedPermissions.length > 0;
              const visible = isNetworkWide(m.role) ? null : `${m.boutiquesVisible} boutique${m.boutiquesVisible > 1 ? "s" : ""} visible${m.boutiquesVisible > 1 ? "s" : ""}`;
              return (
                <IosRow
                  key={m.id}
                  onClick={editable ? () => setEditing(m) : undefined}
                  className="min-h-[64px]"
                  left={<Avatar text={initials(m.user.name)} className="!h-10 !w-10 !text-[13px]" />}
                  right={<RolePill role={m.role} />}
                >
                  <span className="block truncate text-[16px]">
                    {m.user.name}
                    {self && <span className="ml-1 text-muted">(vous)</span>}
                  </span>
                  <span className="block truncate text-[13px] text-muted">{m.user.email}</span>
                  <span className="block truncate text-[13px] text-ink-2">
                    {[scope.main, visible, adjusted ? "permissions ajustées" : null].filter(Boolean).join(" · ")}
                  </span>
                </IosRow>
              );
            })}
          </IosGroup>
        </IosSection>

        {/* Invitations */}
        {manage && (
          <Card pad="px-5 py-[18px]">
            <div className="flex items-center justify-between">
              <SectionTitle>Invitations en attente</SectionTitle>
              <button type="button" onClick={() => setInviting(true)} className="text-[12.5px] font-bold text-accent">
                + Inviter
              </button>
            </div>
            <div className="mt-1 text-[12px] text-muted-2">
              Aucun e-mail n'est envoyé : copiez le lien et transmettez-le à la personne (SMS, messagerie…).
            </div>
            {invitations.isLoading && <Loading />}
            {invitations.error && <ErrorState error={invitations.error} className="mt-3" />}
            {revoke.error && <ErrorState error={revoke.error} className="mt-3" />}
            {invitations.data && pending.length === 0 && (
              <div className="py-6 text-center text-[13px] text-muted">Aucune invitation en attente.</div>
            )}
            <div className="mt-2">
              {pending.map((i) => {
                const scope = scopeLabel(i.role, i.hiboutikAccountIds, i.boutiqueIds, accounts.data ?? [], cityOf);
                return (
                  <div key={i.id} className="flex flex-wrap items-center gap-3 border-t border-line-soft py-3 text-[13px] first:border-t-0">
                    <div className="min-w-[200px] flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{i.email}</span>
                        <RolePill role={i.role} />
                      </div>
                      <div className="mt-[2px] text-[11.5px] text-muted-2">
                        {scope.main} · par {i.invitedBy} · expire le {dateFR(i.expiresAt)}
                      </div>
                    </div>
                    {i.link && <CopyButton text={absoluteLink(i.link)} />}
                    <button
                      type="button"
                      disabled={revoke.isPending}
                      onClick={() => {
                        if (window.confirm(`Révoquer l'invitation de ${i.email} ?`)) revoke.mutate(i.id);
                      }}
                      className="rounded-full px-3 py-[7px] text-[12px] font-bold text-danger hover:bg-danger-bg disabled:opacity-50"
                    >
                      Révoquer
                    </button>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Rôles */}
      <Card pad="px-[22px] py-5">
        <SectionTitle className="mb-3">Les rôles</SectionTitle>
        <div className="flex flex-col gap-3">
          {(Object.keys(ROLE_LABELS) as Role[]).map((r) => (
            <div key={r}>
              <RolePill role={r} />
              <div className="mt-1 text-[12.5px] leading-[1.45] text-muted">{ROLE_DESCRIPTIONS[r]}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-line-soft pt-3 text-[11.5px] leading-[1.5] text-muted-2">
          Chacun ne peut attribuer que des rôles inférieurs au sien, sur son propre périmètre. Un accès par compte Hiboutik inclut
          automatiquement les boutiques ajoutées plus tard à ce compte.
        </div>
      </Card>

      {editing && <MemberDrawer key={editing.id} member={editing} onClose={() => setEditing(null)} />}
      {inviting && <InviteDrawer onClose={() => setInviting(false)} />}
    </div>
  );
}
